import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Sparkles, Palette, CheckCircle2, ArrowRight, Star, Quote, Heart, PhoneCall, ClipboardList, Gem, PartyPopper } from "lucide-react";
import { ASSETS } from "@/data/assets";
import { SectionHeading } from "@/components/ui-bits";
import api from "@/lib/apiClient";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const MEDIA = `${process.env.REACT_APP_BACKEND_URL}/api/media`;

const fadeUp = { hidden: { opacity: 0, y: 32 }, show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } } };
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.12 } } };

const TICKER = [
  "Kashmiri Pandit Weddings", "Gujarati Weddings", "Destination Weddings", "Mehndiraat",
  "Sangeet & Garba", "Devgon & Kasamdry", "Corporate Events", "Birthdays", "Anniversaries", "Wazwan Catering",
];

const usps = [
  { icon: CheckCircle2, title: "End-to-End Solutions", text: "From venue to vidaai — every detail managed under one roof so you never juggle vendors again." },
  { icon: Palette, title: "Tailored Creativity", text: "Bespoke themes, decor and experiences designed around your story, culture and budget." },
  { icon: Sparkles, title: "Flawless Execution", text: "A seasoned team ensures precision timing and a stress-free celebration on the day that matters most." },
];

const featured = [
  { title: "Weddings & Destination Weddings", img: ASSETS.mandap, tag: "Social Celebrations" },
  { title: "Corporate Events & Launches", img: ASSETS.corporate, tag: "Corporate" },
  { title: "Birthdays & Anniversaries", img: ASSETS.birthday, tag: "Celebrations" },
  { title: "Decor, Stage & Production", img: ASSETS.floral, tag: "Production & Design" },
];

const celebrations = [
  { title: "Weddings", img: ASSETS.mandap },
  { title: "Destination Weddings", img: `${MEDIA}/couple.webp` },
  { title: "Corporate Events", img: ASSETS.corporate },
  { title: "Birthdays", img: ASSETS.birthday },
  { title: "Anniversaries & Engagements", img: ASSETS.chandelier },
  { title: "Social & Cultural Shows", img: ASSETS.floral },
];

const stats = [
  { value: 500, suffix: "+", label: "Events Delivered" },
  { value: 300, suffix: "+", label: "Weddings Planned" },
  { value: 12, suffix: "+", label: "Years of Joy" },
  { value: 20, suffix: "+", label: "Cities Covered" },
];

const PROCESS = [
  { icon: PhoneCall, title: "Consult & Dream", text: "Share your vision, dates and budget — we listen closely and understand every detail." },
  { icon: ClipboardList, title: "Plan & Design", text: "We craft a tailored concept, curate the best vendors and design your perfect event." },
  { icon: Gem, title: "Coordinate & Execute", text: "Our team manages logistics, decor and timing so everything runs seamlessly." },
  { icon: PartyPopper, title: "Celebrate & Cherish", text: "You stay fully present and make memories while we handle the rest." },
];

const FAQS = [
  { q: "Which cities and regions do you cover?", a: "We are based in Jammu and plan events across Jammu & Kashmir and pan-India, including destination weddings in Rajasthan, Goa, Himachal, Kerala and beyond." },
  { q: "What types of events do you manage?", a: "Weddings of every tradition, engagements, mehndi & sangeet, anniversaries, birthdays and baby showers, plus corporate conferences, product launches, dealer meets and award nights." },
  { q: "Do you handle every part of the wedding?", a: "Yes — we are a true One Stop Solution: venue, catering, decor & floral, stage/mandap, photography & video, makeup & mehandi, transport, boarding & lodging, pandit ji, card printing and more." },
  { q: "How far in advance should we book?", a: "For weddings we recommend booking 3–6 months ahead, though we also manage shorter timelines. Reach out early so we can secure your preferred dates and venues." },
  { q: "Can you work within our budget?", a: "Absolutely. We design tailored packages for a wide range of budgets and stay transparent about costs at every step." },
  { q: "How do we get started?", a: "Simply fill out the booking form, call us, or tap the WhatsApp button — our team will reach out to craft a personalised plan for your event." },
];

function CountStat({ value, suffix, label }) {
  const ref = useRef(null);
  const [n, setN] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        let cur = 0;
        const step = Math.max(1, value / 45);
        const id = setInterval(() => {
          cur += step;
          if (cur >= value) { cur = value; clearInterval(id); }
          setN(Math.round(cur));
        }, 24);
        io.disconnect();
      }
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, [value]);
  return (
    <div ref={ref} className="text-center">
      <p className="font-serif text-4xl sm:text-5xl text-brand-gold">{n}{suffix}</p>
      <p className="text-xs sm:text-sm uppercase tracking-[0.18em] text-white/70 mt-2">{label}</p>
    </div>
  );
}

function TestiCard({ t }) {
  return (
    <div className="w-[300px] sm:w-[360px] shrink-0 border border-brand-navy/10 bg-white p-6 rounded-sm relative">
      <Quote className="text-brand-gold/25 absolute top-4 right-4" size={34} />
      <div className="flex gap-1 text-brand-gold">
        {Array.from({ length: t.rating || 5 }).map((_, k) => <Star key={k} size={14} fill="currentColor" />)}
      </div>
      <p className="mt-4 text-sm text-brand-ink/75 leading-relaxed italic line-clamp-4">“{t.quote}”</p>
      <div className="mt-5 pt-4 border-t border-brand-navy/10">
        <p className="font-serif text-lg text-brand-navy">{t.name}</p>
        <p className="overline mt-0.5">{t.event_type}</p>
      </div>
    </div>
  );
}

export default function Home() {
  const { data: testimonials = [] } = useQuery({ queryKey: ["testimonials"], queryFn: async () => (await api.get("/testimonials")).data });
  const { data: content = {} } = useQuery({ queryKey: ["site-content"], queryFn: async () => (await api.get("/site-content")).data });
  const rowA = testimonials.slice(0, Math.ceil(testimonials.length / 2));
  const rowB = testimonials.slice(Math.ceil(testimonials.length / 2));

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <img src={`${MEDIA}/couple.webp`} alt="Grand Indian wedding celebration" className="absolute inset-0 w-full h-full object-cover animate-kenburns" onError={(e) => { e.currentTarget.src = ASSETS.hero; }} />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-navy via-brand-navy/85 to-brand-navy/30" />
        <div className="absolute -top-20 -left-16 w-72 h-72 rounded-full bg-brand-gold/10 blur-3xl animate-floaty" />

        <motion.div variants={stagger} initial="hidden" animate="show" className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-28 pb-24 w-full">
          <motion.p variants={fadeUp} className="overline mb-4">Jammu · Pan-India · Destination</motion.p>
          <motion.h1 variants={fadeUp} className="font-serif text-white text-5xl sm:text-6xl lg:text-7xl leading-[1.03] tracking-tight max-w-4xl">
            We Create.<br /><span className="text-shine italic">You Celebrate.</span>
          </motion.h1>
          <motion.p variants={fadeUp} className="mt-6 text-white/80 text-base sm:text-lg max-w-xl leading-relaxed">
            {content.hero_subtitle || "Jammu's trusted One Stop Solution for weddings, social celebrations and corporate events — from intimate gatherings to grand destination celebrations, crafted with elegance and executed to perfection."}
          </motion.p>
          <motion.div variants={fadeUp} className="mt-9 flex flex-wrap gap-4">
            <Link to="/booking" data-testid="hero-book-btn" className="group bg-brand-gold text-brand-navy font-semibold px-7 py-3.5 rounded-sm flex items-center gap-2 hover:-translate-y-0.5 transition-transform shadow-lg shadow-brand-gold/20">
              Plan Your Dream Event <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/gallery" data-testid="hero-gallery-btn" className="border border-white/30 text-white px-7 py-3.5 rounded-sm hover:bg-white/10 transition-colors backdrop-blur-sm">
              View Our Work
            </Link>
          </motion.div>
          <motion.div variants={fadeUp} className="mt-12 flex flex-wrap gap-x-10 gap-y-4">
            {["500+ Events", "300+ Weddings", "4.9★ Rated", "12+ Years"].map((s) => (
              <div key={s} className="flex items-center gap-2 text-white/85 text-sm">
                <Heart size={15} className="text-brand-gold" fill="currentColor" /> {s}
              </div>
            ))}
          </motion.div>
        </motion.div>

        <div className="absolute bottom-7 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/60 animate-floaty">
          <span className="text-[10px] tracking-[0.3em] uppercase">Scroll</span>
          <span className="w-px h-8 bg-gradient-to-b from-brand-gold to-transparent" />
        </div>
      </section>

      {/* GOLD TICKER */}
      <div className="bg-brand-gold text-brand-navy py-3.5 overflow-hidden marquee-track">
        <div className="flex whitespace-nowrap animate-ticker">
          {[...TICKER, ...TICKER].map((t, i) => (
            <span key={i} className="mx-6 text-sm font-semibold uppercase tracking-widest flex items-center gap-3">
              {t} <Sparkles size={14} />
            </span>
          ))}
        </div>
      </div>

      {/* ABOUT SNIPPET */}
      <section className="py-24 max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} className="relative">
          <img src={`${MEDIA}/haldi.webp`} alt="Haldi ceremony" className="w-full h-[460px] object-cover rounded-sm" onError={(e) => { e.currentTarget.src = ASSETS.chandelier; }} />
          <div className="absolute -bottom-6 -right-4 sm:right-6 bg-brand-navy text-white px-7 py-6 rounded-sm shadow-xl animate-floaty">
            <p className="font-serif text-3xl text-brand-gold">12+</p>
            <p className="text-xs uppercase tracking-widest mt-1 text-white/70">Years of Celebrations</p>
          </div>
        </motion.div>
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }}>
          <SectionHeading overline="Who We Are" title={content.home_about_title || "Turning milestones into memories that last a lifetime."}
            subtitle={content.home_about_subtitle || "WEDDING EVENTS is a Jammu-based event & wedding management agency. We bring together creativity, meticulous planning and flawless hospitality for weddings of every tradition, social celebrations and corporate events — so you can be fully present in every moment."} />
          <Link to="/about" data-testid="home-about-link" className="mt-7 inline-flex items-center gap-2 text-brand-navy font-semibold border-b-2 border-brand-gold pb-1 hover:gap-3 transition-all">
            Discover Our Story <ArrowRight size={18} />
          </Link>
        </motion.div>
      </section>

      {/* STATS BAND */}
      <section className="relative py-16 overflow-hidden">
        <img src={ASSETS.chandelier} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-brand-navy/90" />
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 grid grid-cols-2 md:grid-cols-4 gap-10">
          {stats.map((s) => <CountStat key={s.label} {...s} />)}
        </div>
      </section>

      {/* WHAT WE PLAN */}
      <section className="py-24 max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHeading overline="What We Plan" title="Every celebration, beautifully done" align="center"
          subtitle="Weddings of every tradition, destination celebrations, corporate events and joyful social milestones — all handled end-to-end by one dedicated team." />
        <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }} className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {celebrations.map((c) => (
            <motion.div key={c.title} variants={fadeUp}>
              <Link to="/services" className="group block relative h-72 overflow-hidden rounded-sm">
                <img src={c.img} alt={c.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" onError={(e) => { e.currentTarget.src = ASSETS.mandap; }} />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/90 via-brand-navy/25 to-transparent" />
                <div className="absolute bottom-0 p-6 flex items-center justify-between w-full">
                  <h3 className="font-serif text-2xl text-white">{c.title}</h3>
                  <span className="w-9 h-9 rounded-full bg-brand-gold text-brand-navy grid place-items-center opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all"><ArrowRight size={16} /></span>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-24 bg-brand-navy relative grain">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHeading overline="Why Choose Us" title="A partner you can trust with your biggest days." light />
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} className="mt-14 grid md:grid-cols-3 gap-6">
            {usps.map((u) => (
              <motion.div key={u.title} variants={fadeUp} className="h-full border border-white/10 p-8 rounded-sm hover:border-brand-gold/50 hover:-translate-y-2 transition-all bg-white/[0.02]">
                <u.icon className="text-brand-gold" size={34} />
                <h3 className="font-serif text-2xl text-white mt-5">{u.title}</h3>
                <p className="mt-3 text-white/65 text-sm leading-relaxed">{u.text}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* FEATURED SERVICES */}
      <section className="py-24 max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHeading overline="What We Do" title="Featured services" />
          <Link to="/services" className="text-brand-navy font-semibold flex items-center gap-2 border-b-2 border-brand-gold pb-1 hover:gap-3 transition-all">
            All Services <ArrowRight size={18} />
          </Link>
        </div>
        <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }} className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {featured.map((f) => (
            <motion.div key={f.title} variants={fadeUp}>
              <Link to="/services" className="group block relative h-80 overflow-hidden rounded-sm">
                <img src={f.img} alt={f.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/90 via-brand-navy/20 to-transparent" />
                <div className="absolute bottom-0 p-6 group-hover:pb-8 transition-all">
                  <p className="overline">{f.tag}</p>
                  <h3 className="font-serif text-xl text-white mt-2 leading-snug">{f.title}</h3>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* HOW WE WORK */}
      <section className="py-24 bg-brand-ivory">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHeading overline="How We Work" title="From first hello to happy tears" align="center"
            subtitle="A simple, transparent journey — we do the heavy lifting so you enjoy every single moment." />
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }} className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {PROCESS.map((p, i) => (
              <motion.div key={p.title} variants={fadeUp} className="relative text-center group">
                <div className="relative mx-auto w-20 h-20 rounded-full bg-white border border-brand-gold/40 grid place-items-center shadow-sm group-hover:-translate-y-1.5 transition-transform">
                  <p.icon className="text-brand-navy" size={30} />
                  <span className="absolute -top-2 -right-1 w-7 h-7 rounded-full bg-brand-gold text-brand-navy text-sm font-bold grid place-items-center">{i + 1}</span>
                </div>
                <h3 className="font-serif text-xl text-brand-navy mt-6">{p.title}</h3>
                <p className="mt-2 text-sm text-brand-ink/70 leading-relaxed">{p.text}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* TESTIMONIAL MARQUEE */}
      <section className="py-24 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHeading overline="Kind Words" title="Loved by families & brands across India" align="center"
            subtitle="Real stories from the families and brands who trusted us with their most precious moments." />
        </div>
        <div className="mt-14 space-y-5 marquee-track">
          <div className="flex gap-5 w-max animate-marquee">
            {[...rowA, ...rowA].map((t, i) => <TestiCard key={`a-${i}`} t={t} />)}
          </div>
          <div className="flex gap-5 w-max animate-marquee-rev">
            {[...rowB, ...rowB].map((t, i) => <TestiCard key={`b-${i}`} t={t} />)}
          </div>
        </div>
        <div className="text-center mt-14">
          <Link to="/testimonials" className="text-brand-navy font-semibold border-b-2 border-brand-gold pb-1">Read all testimonials</Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 max-w-3xl mx-auto px-5 sm:px-8">
        <SectionHeading overline="Good to Know" title="Frequently asked questions" align="center" />
        <div className="mt-12">
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`} className="border-brand-navy/10">
                <AccordionTrigger className="font-serif text-lg text-brand-navy py-5 hover:no-underline text-left">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-brand-ink/70 leading-relaxed text-base">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-28 overflow-hidden">
        <img src={ASSETS.mandap} alt="Grand wedding mandap" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-brand-navy/80" />
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="relative max-w-3xl mx-auto px-5 text-center">
          <h2 className="font-serif text-4xl sm:text-5xl text-white leading-tight">Let's create your unforgettable day.</h2>
          <p className="mt-5 text-white/75">Tell us about your event and our team will craft a tailored plan for you.</p>
          <Link to="/booking" data-testid="cta-book-btn" className="mt-8 inline-flex bg-brand-gold text-brand-navy font-semibold px-8 py-3.5 rounded-sm hover:-translate-y-0.5 transition-transform shadow-lg shadow-brand-gold/20">
            Start Planning
          </Link>
        </motion.div>
      </section>
    </div>
  );
}
